import {
    Avatar,
    Box,
    Button,
    CardActions,
    CardHeader,
    Divider,
    Icon,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';

import { SvgIconComponent } from '@mui/icons-material';
import { StateLayer, getStateLayerColor } from '@M3/utils/getStayeLayerColor';
import { routes } from '@data/routes';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useTranslation } from 'react-i18next';
import { Link, useChildMatches, useLocation } from '@tanstack/react-router';
import useApi from '@hooks/useApi';

const DrawerItem = ({
    name,
    Icon,
    path,
    selected,
}: {
    name: string;
    Icon: SvgIconComponent;
    path: string;
    selected: boolean;
}) => {
    // const selected = useChildMatches({
    //     select: match => match[0]?.pathname === path,
    // });

    return (
        <ListItem
            sx={{
                flexDirection: 'column',
                color: 'onPrimary.main',
                padding: 0,
                marginBottom: 1.5,
                cursor: 'pointer',
                '&:hover': {
                    '& .MuiListItemButton-root': {
                        backgroundColor: theme =>
                            getStateLayerColor(
                                StateLayer.Hover,
                                theme.palette.primary.main,
                                theme.palette.onPrimary.main,
                            ),

                        color: theme =>
                            getStateLayerColor(
                                StateLayer.Hover,
                                theme.palette.onPrimary.main,
                                theme.palette.primary.main,
                            ),
                    },
                },
            }}
        >
            <ListItemButton
                component={Link}
                to={path}
                selected={selected}
                sx={{
                    color: 'inherit',
                    height: 32,
                    paddingX: 1.5,
                    '&:hover': {
                        color: 'inherit',
                    },
                    '&:active': {
                        backgroundColor: theme =>
                            getStateLayerColor(
                                StateLayer.Press,
                                theme.palette.primary.main,
                                theme.palette.onPrimary.main,
                            ),
                        color: theme =>
                            getStateLayerColor(
                                StateLayer.Press,
                                theme.palette.onPrimary.main,
                                theme.palette.primary.main,
                            ),
                    },
                    '&.Mui-selected': {
                        color: theme => theme.palette.onPrimary.main,
                        background: theme =>
                            getStateLayerColor(
                                StateLayer.Focus * 2,
                                theme.palette.primary.main,
                                theme.palette.onPrimary.main,
                            ),
                        '&:hover': {
                            backgroundColor: theme =>
                                getStateLayerColor(
                                    StateLayer.Hover,
                                    theme.palette.primary.main,
                                    theme.palette.onPrimary.main,
                                ),
                            color: theme =>
                                getStateLayerColor(
                                    StateLayer.Hover,
                                    theme.palette.onPrimary.main,
                                    theme.palette.primary.main,
                                ),
                        },
                        '&:active': {
                            backgroundColor: theme =>
                                getStateLayerColor(
                                    StateLayer.Press,
                                    theme.palette.primary.main,
                                    theme.palette.onPrimary.main,
                                ),
                            color: theme =>
                                getStateLayerColor(
                                    StateLayer.Press,
                                    theme.palette.onPrimary.main,
                                    theme.palette.primary.main,
                                ),
                        },
                    },
                }}
            >
                <ListItemIcon
                    sx={{
                        justifyContent: 'center',
                    }}
                >
                    <Icon />
                </ListItemIcon>
            </ListItemButton>
            <ListItemText
                primaryTypographyProps={{
                    sx: {
                        fontSize: theme => theme.typography.caption.fontSize,
                        fontWeight: 'fontWeightMedium',
                        ...(selected && {
                            fontWeight: 'fontWeightBold',
                        }),
                    },
                }}
            >
                {name}
            </ListItemText>
        </ListItem>
    );
};

const SettingsPopper = () => {
    const { t } = useTranslation('controls');
    const authInfo = useAuthInfo();
    const { activeOrg } = useApi();
    const logout = useLogoutFunction();

    return (
        <>
            <CardHeader
                title={activeOrg.orgName}
                subheader={authInfo.user?.email}
                avatar={
                    <Avatar
                        src={authInfo.user?.pictureUrl}
                        sx={{
                            p: 0.25,
                            backgroundColor: 'primary.main',
                        }}
                    />
                }
            />
            <Divider />
            <CardActions sx={{ justifyContent: 'space-between' }}>
                <Button variant="text" LinkComponent={Link} to="/settings">
                    {t('Settings')}
                </Button>
                <Button
                    variant="text"
                    color="secondary"
                    endIcon={<Icon>logout</Icon>}
                    onClick={logout}
                >
                    {t('Logout')}
                </Button>
            </CardActions>
        </>
    );
};

const NavigationRail = () => {
    const { t } = useTranslation('controls');
    const authInfo = useAuthInfo();
    const [, mainRoute] = useLocation().pathname.split('/');

    return (
        <Box width={80} display="flex" flexDirection="column">
            <Box
                component={Link}
                to="/contacts/"
                sx={{
                    backgroundColor: 'surface.main',
                    borderRadius: 3,
                    width: 'max-content',
                    marginX: 'auto',
                    marginY: 2,
                    display: 'flex',
                }}
            >
                <img
                    src="/logo.svg"
                    alt="logo"
                    style={{
                        width: 48,
                        padding: 6,
                    }}
                />
            </Box>

            <List sx={{ my: 2 }}>
                {routes.map(({ name, icon, path }) => (
                    <DrawerItem
                        key={name}
                        name={t(name)}
                        Icon={icon}
                        path={path}
                        selected={mainRoute === path.split('/')[1]}
                    />
                ))}
            </List>

            <Tooltip
                title={<SettingsPopper />}
                placement="right"
                arrow
                slotProps={{
                    arrow: {
                        sx: {
                            color: 'surface.main',
                        },
                    },
                    tooltip: {
                        sx: {
                            padding: 0,
                            backgroundColor: 'surface.main',
                        },
                    },
                }}
            >
                <Avatar
                    component={Link}
                    to="/settings/"
                    sx={{
                        mx: 'auto',
                        mt: 'auto',
                        height: 52,
                        width: 52,
                        mb: 8,
                        backgroundColor: 'secondaryContainer.main',
                        p: 0.25,
                        cursor: 'pointer',
                    }}
                >
                    <img
                        src={authInfo.user?.pictureUrl}
                        alt="profile"
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </Avatar>
            </Tooltip>
        </Box>
    );
};

export default NavigationRail;
